import { useState, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  CheckCircle,
  Clock,
  XCircle,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
} from 'lucide-react';
import { useOrderStats, useOrderTimeline, useOrderFailures } from '../../hooks/useOrders';
import { useLender } from '../../hooks/useLenders';
import { useUrnResolver } from '../../hooks/useUrnResolver';
import OrdersChart from '../../components/dashboard/OrdersChart';
import FailuresPanel from '../../components/dashboard/FailuresPanel';
import TimeRangeSelector, {
  computeRange,
  type TimeRangeKey,
  type TimeRange,
} from '../../components/dashboard/TimeRangeSelector';

const STATUS_GROUPS = [
  { key: 'completed', label: 'Completed', statuses: ['completed'], icon: CheckCircle, color: 'text-emerald-600 dark:text-emerald-400', dot: 'bg-emerald-500' },
  { key: 'processing', label: 'Processing', statuses: ['processing', 'pending', 'queued', 'created'], icon: Clock, color: 'text-amber-600 dark:text-amber-400', dot: 'bg-amber-500' },
  { key: 'failed', label: 'Failed', statuses: ['failed', 'error'], icon: XCircle, color: 'text-red-600 dark:text-red-400', dot: 'bg-red-500' },
  { key: 'canceled', label: 'Canceled', statuses: ['canceled', 'canceling', 'cancel.submitted', 'declined', 'withdrawn'], icon: AlertTriangle, color: 'text-gray-500 dark:text-gray-400', dot: 'bg-gray-400' },
];

function previousPeriodRange(range: TimeRange): { from?: string; to?: string } {
  if (!range.from || !range.to) return {};
  const fromMs = new Date(range.from).getTime();
  const toMs = new Date(range.to).getTime();
  const duration = toMs - fromMs;
  return { from: new Date(fromMs - duration).toISOString(), to: range.from };
}

export default function LenderReportsPage() {
  const { urn } = useParams<{ urn: string }>();
  const decodedUrn = decodeURIComponent(urn!);
  const navigate = useNavigate();
  const { resolveProductUrn } = useUrnResolver();
  const { data: lender } = useLender(decodedUrn);

  const [rangeKey, setRangeKey] = useState<TimeRangeKey>('7d');
  const [range, setRange] = useState<TimeRange>(() => computeRange('7d'));
  const handleRangeChange = useCallback((key: TimeRangeKey, r: TimeRange) => { setRangeKey(key); setRange(r); }, []);

  // Stats for this lender
  const statsParams = useMemo(() => {
    const p: { originator: string; from?: string; to?: string } = { originator: decodedUrn };
    if (range.from && range.to) { p.from = range.from; p.to = range.to; }
    return p;
  }, [decodedUrn, range]);
  const { data: stats } = useOrderStats(statsParams);

  // Previous period stats for comparison
  const prevRange = useMemo(() => previousPeriodRange(range), [range]);
  const prevStatsParams = useMemo(() => {
    if (!prevRange.from || !prevRange.to) return undefined;
    return { originator: decodedUrn, from: prevRange.from, to: prevRange.to };
  }, [decodedUrn, prevRange]);
  const { data: prevStats } = useOrderStats(prevStatsParams);

  // Timeline for this lender
  const timelineParams = useMemo(() => {
    const now = new Date();
    const ago = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    return {
      from: range.from || ago.toISOString(),
      to: range.to || now.toISOString(),
      bucketMinutes: range.bucketMinutes,
      originator: decodedUrn,
    };
  }, [decodedUrn, range]);
  const { data: timelineData, isLoading: timelineLoading } = useOrderTimeline(timelineParams);

  // Failures for this lender
  const failuresParams = useMemo(() => {
    const now = new Date();
    const ago = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    const rangeFrom = range.from ? new Date(range.from) : ago;
    const effectiveFrom = rangeFrom < ago ? rangeFrom : ago;
    return {
      from: effectiveFrom.toISOString(),
      to: range.to || now.toISOString(),
      limit: 20,
      originator: decodedUrn,
    };
  }, [decodedUrn, range]);
  const { data: failuresData, isLoading: failuresLoading } = useOrderFailures(failuresParams);

  // Status summary cards
  const statusCards = useMemo(() => {
    const byStatus = stats?.byStatus || {};
    const prevByStatus = prevStats?.byStatus || {};
    return STATUS_GROUPS.map((group) => {
      const count = group.statuses.reduce((sum, s) => sum + (byStatus[s] || 0), 0);
      const prevCount = group.statuses.reduce((sum, s) => sum + (prevByStatus[s] || 0), 0);
      const delta = prevStats ? count - prevCount : null;
      return { ...group, count, delta };
    });
  }, [stats, prevStats]);

  // Product breakdown
  const productBreakdown = useMemo(() => {
    if (!stats?.byProduct) return [];
    return Object.entries(stats.byProduct)
      .sort(([, a], [, b]) => b - a)
      .map(([urn, count]) => ({
        urn,
        name: resolveProductUrn(urn),
        count,
      }));
  }, [stats, resolveProductUrn]);

  // Status breakdown
  const statusBreakdown = useMemo(() => {
    if (!stats?.byStatus) return [];
    return Object.entries(stats.byStatus)
      .sort(([, a], [, b]) => b - a)
      .map(([status, count]) => ({
        status,
        count,
      }));
  }, [stats]);

  return (
    <div className="space-y-4 lg:space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <button
              onClick={() => navigate(`/lenders/${encodeURIComponent(decodedUrn)}`)}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {lender?.displayName || lender?.name || 'Lender'} - Reports
            </h1>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 ml-7">
            {(stats?.total ?? 0).toLocaleString()} orders tracked for this lender
          </p>
        </div>
        <TimeRangeSelector value={rangeKey} onChange={handleRangeChange} />
      </div>

      {/* Status cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {statusCards.map((group) => (
          <div
            key={group.key}
            className="card p-4 text-left w-full"
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full ${group.dot}`} />
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400">{group.label}</span>
              </div>
              {group.delta !== null && group.delta !== 0 && (
                <span className={`inline-flex items-center gap-0.5 text-[10px] font-medium ${group.delta > 0 ? group.color : 'text-gray-400'}`}>
                  {group.delta > 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                  {group.delta > 0 ? '+' : ''}{group.delta}
                </span>
              )}
            </div>
            <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{group.count.toLocaleString()}</p>
          </div>
        ))}
      </div>

      {/* Chart */}
      <OrdersChart
        from={range.from}
        to={range.to}
        buckets={timelineData?.buckets ?? []}
        timelineLoading={timelineLoading}
        originator={decodedUrn}
      />

      {/* Breakdowns */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* By Product */}
        <div className="card overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-100 dark:border-gray-800">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Orders by Product</h3>
          </div>
          {productBreakdown.length === 0 ? (
            <div className="py-8 text-center text-xs text-gray-400 dark:text-gray-500">No data</div>
          ) : (
            <div className="divide-y divide-gray-50 dark:divide-gray-800/50">
              {productBreakdown.map((item) => (
                <div key={item.urn} className="flex items-center justify-between px-5 py-2.5">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-gray-900 dark:text-gray-100 truncate">{item.name}</p>
                    <p className="text-[10px] font-mono text-gray-400 dark:text-gray-500 truncate">{item.urn}</p>
                  </div>
                  <span className="text-sm font-semibold text-gray-900 dark:text-gray-100 ml-4">
                    {item.count.toLocaleString()}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* By Status */}
        <div className="card overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-100 dark:border-gray-800">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Orders by Status</h3>
          </div>
          {statusBreakdown.length === 0 ? (
            <div className="py-8 text-center text-xs text-gray-400 dark:text-gray-500">No data</div>
          ) : (
            <div className="divide-y divide-gray-50 dark:divide-gray-800/50">
              {statusBreakdown.map((item) => {
                const total = stats?.total || 1;
                const pct = Math.round((item.count / total) * 100);
                return (
                  <div key={item.status} className="px-5 py-2.5">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-gray-900 dark:text-gray-100 capitalize">
                        {item.status.replace(/[._]/g, ' ')}
                      </span>
                      <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                        {item.count.toLocaleString()}
                        <span className="text-xs font-normal text-gray-400 ml-1">({pct}%)</span>
                      </span>
                    </div>
                    <div className="w-full h-1.5 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full bg-blue-500"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Failures */}
      <FailuresPanel failures={failuresData?.failures ?? []} isLoading={failuresLoading} />
    </div>
  );
}
